// Sessions tab — paginated chat sessions grid with server-side
// pagination + a small KPI block.
//
// Mirrors web/scripts/admin.js's loadSessions() / SESSION_COLUMN_DEFS.
// Row click opens the conversation drawer (TODO — defer to a later
// commit). For PR-A1 the row click is a soft no-op so the tab works
// end-to-end as a read-only view.

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import type { OrgScope, SessionRow } from "../../api/admin";
import { adminApi } from "../../api/admin";
import { SessionDrawer } from "./SessionDrawer";
import {
  Chart,
  ErrorBlock,
  KpiCard,
  LoadingBlock,
  SourceBadge,
  formatDateTime,
  formatNumber,
  formatPercent,
  horizontalBarOption,
} from "./_shared";

const PAGE_SIZE = 50;

const SOURCES = ["", "web", "mcp", "slack", "sdk", "gemini", "terminal"];

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function SessionsTab({ bearer, scope }: Props) {
  const [source, setSource] = useState<string>("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [overviewKpis, setOverviewKpis] = useState<{
    total_30d?: number;
    avg_queries_per_session?: number;
    error_rate_30d?: number;
    top_datasources_30d?: Array<{ datasource_name: string; count: number }>;
  } | null>(null);
  const [overviewError, setOverviewError] = useState<Error | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState<SessionRow | null>(null);

  // Reset to page 1 whenever the source filter changes.
  useEffect(() => {
    setPage(1);
  }, [source]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .overview(bearer, scope)
      .then((d) => {
        if (!cancelled) setOverviewKpis(d.sessions_kpis ?? {});
      })
      .catch((e: Error) => {
        if (!cancelled) setOverviewError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .sessions(bearer, { ...scope, source: source || undefined, page, page_size: PAGE_SIZE })
      .then((d) => {
        if (cancelled) return;
        setRows(d.items || []);
        setTotal(d.total || 0);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, source, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  const colDefs = useMemo<ColDef<SessionRow>[]>(
    () => [
      {
        field: "started_at",
        headerName: "Started",
        width: 180,
        sort: "desc",
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      { field: "user_email", headerName: "User", flex: 1 },
      { field: "organization", headerName: "Org", width: 140 },
      {
        field: "source",
        headerName: "Source",
        width: 100,
        cellRenderer: (p: { value: string }) => <SourceBadge source={p.value} />,
      },
      { field: "datasource_name", headerName: "Datasource", width: 160 },
      { field: "query_count", headerName: "Queries", width: 90, type: "numericColumn" },
      {
        field: "error_count",
        headerName: "Errors",
        width: 80,
        type: "numericColumn",
        cellStyle: (p) =>
          (p.value as number) > 0 ? { color: "#b91c1c", fontWeight: 600 } : undefined,
      },
      { field: "cache_hits", headerName: "Cache hits", width: 100, type: "numericColumn" },
      {
        field: "avg_time_ms",
        headerName: "Avg time",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) =>
          p.value != null ? `${Number(p.value).toFixed(0)} ms` : "—",
      },
      {
        field: "avg_quality_score",
        headerName: "Quality",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) =>
          p.value != null ? `${(Number(p.value) * 100).toFixed(0)}%` : "—",
      },
      {
        field: "total_tokens",
        headerName: "Tokens",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : "—"),
      },
      {
        field: "total_cost_usd",
        headerName: "Cost",
        width: 90,
        type: "numericColumn",
        valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(4)}` : "—"),
      },
      {
        field: "last_activity_at",
        headerName: "Last activity",
        width: 180,
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      {
        field: "session_id",
        headerName: "Session ID",
        width: 130,
        valueFormatter: (p) => (p.value ? `${String(p.value).slice(0, 8)}…` : "—"),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Sessions (30d)" value={formatNumber(overviewKpis?.total_30d)} />
        <KpiCard
          label="Avg Queries / Session"
          value={
            overviewKpis?.avg_queries_per_session != null
              ? overviewKpis.avg_queries_per_session.toFixed(1)
              : "—"
          }
        />
        <KpiCard
          label="Session Error Rate (30d)"
          value={formatPercent(overviewKpis?.error_rate_30d)}
        />
        <KpiCard label="Top Datasources (30d)" value="">
          {overviewKpis?.top_datasources_30d && overviewKpis.top_datasources_30d.length > 0 ? (
            <Chart
              height={60}
              option={horizontalBarOption(
                overviewKpis.top_datasources_30d.map((d) => ({
                  label: d.datasource_name,
                  value: d.count,
                })),
              )}
            />
          ) : (
            <div className="text-xs text-neutral-400">—</div>
          )}
        </KpiCard>
      </div>
      {overviewError && <ErrorBlock error={overviewError} />}

      <div className="flex items-end gap-3">
        <div>
          <label className="block text-xs text-neutral-500 mb-1" htmlFor="sessions-source-filter">
            Source
          </label>
          <select
            id="sessions-source-filter"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="h-8 px-2 pr-7 text-sm rounded-md border border-line bg-white text-neutral-800 hover:bg-chip focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s || "All sources"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <ErrorBlock error={error} />}
      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <div className="ag-theme-quartz" style={{ width: "100%", height: 540 }}>
            <AgGridReact
              rowData={rows}
              columnDefs={colDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                if (e.data) setActiveSession(e.data);
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-3 text-xs text-neutral-600">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-md border border-line px-3 h-8 hover:bg-chip disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span>
              {start}–{end} of {total} · Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-line px-3 h-8 hover:bg-chip disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
      <SessionDrawer
        bearer={bearer}
        scope={scope}
        session={activeSession}
        onClose={() => setActiveSession(null)}
      />
    </div>
  );
}
